import type { Transform } from './types.js';

/**
 * Laying out N photo slots on a canvas, for any N.
 *
 * A collage product is sold by its count — 9, 15, 16, 24, 30 — and the count is
 * a merchandising decision, not a mathematical one. Rows-times-columns only
 * serves the composite numbers; a 13-photo product exists the day the owner
 * wants one, and its last row simply holds fewer cells, centred, the way every
 * printed collage solves it. One function owns that arithmetic so the editor's
 * generator and anything after it lay identical grids.
 */

export interface PhotoLayoutOptions {
  /** Gap between cells, as a fraction of the shorter canvas edge (0.02 = 2%). */
  gap?: number;
  /** Border around the whole grid, same unit. */
  margin?: number;
}

/**
 * Cell placements for `count` photos, row-major, last partial row centred.
 *
 * The column count follows the canvas: a tall poster takes fewer columns than a
 * wide banner for the same N, so cells stay near-square instead of becoming
 * ribbons. Cells are uniform — a collage reads as a grid because every cell
 * matches; "make the last three bigger" is a design decision the editor is for.
 */
export function planPhotoLayout(
  count: number,
  width: number,
  height: number,
  options: PhotoLayoutOptions = {},
): Transform[] {
  if (count <= 0 || width <= 0 || height <= 0) return [];
  const short = Math.min(width, height);
  const margin = (options.margin ?? 0.04) * short;
  const gap = (options.gap ?? 0.02) * short;

  const innerW = Math.max(1, width - margin * 2);
  const innerH = Math.max(1, height - margin * 2);

  // Columns that make cells closest to square: for near-square cells,
  // cols/rows ≈ innerW/innerH, and rows = ceil(count/cols).
  let cols = Math.max(1, Math.round(Math.sqrt((count * innerW) / innerH)));
  cols = Math.min(cols, count);
  // Never leave a column that nothing uses (count=7, cols=4 → rows=2 uses all 4;
  // count=5, cols=3 → rows=2, fine; but count=3, cols=2 → rows=2 with a lone
  // cell — acceptable and centred. What must not happen is cols > count.)
  const rows = Math.ceil(count / cols);

  const cellW = (innerW - gap * (cols - 1)) / cols;
  const cellH = (innerH - gap * (rows - 1)) / rows;

  const cells: Transform[] = [];
  for (let r = 0; r < rows; r++) {
    const inRow = Math.min(cols, count - r * cols);
    // The last row centres its remainder; full rows start at the margin.
    const rowWidth = inRow * cellW + (inRow - 1) * gap;
    const startX = margin + (innerW - rowWidth) / 2;
    for (let c = 0; c < inRow; c++) {
      cells.push({
        cx: startX + c * (cellW + gap) + cellW / 2,
        cy: margin + r * (cellH + gap) + cellH / 2,
        w: cellW,
        h: cellH,
      });
    }
  }
  return cells;
}

/**
 * Serbian counting for photos: 2–4 take "slike", the rest "slika", with the
 * teens always "slika" — "22 slike" but "12 slika". The label a layout choice
 * shows the shopper has to be grammatical or the whole product reads foreign.
 */
export function photoCountLabel(n: number): string {
  const tens = n % 100;
  const ones = n % 10;
  const form = ones >= 2 && ones <= 4 && !(tens >= 12 && tens <= 14) ? 'slike' : 'slika';
  return `${n} ${form}`;
}
